import { Menu } from '@mui/material'
import { FeatureFlagIdSendReminder } from 'c1gen/features'
import {
    TaskV1TaskActionsServiceApprove,
    TaskV1TaskActionsServiceClose,
    TaskV1TaskActionsServiceComment,
    TaskV1TaskActionsServiceDeny,
    TaskV1TaskActionsServiceHardReset,
    TaskV1TaskActionsServiceMarkProvisionComplete,
    TaskV1TaskActionsServiceMarkProvisionErrored,
    TaskV1TaskActionsServiceProcessNow,
    TaskV1TaskActionsServiceReassign,
    TaskV1TaskActionsServiceRestart,
    TaskV1TaskActionsServiceSkipStep,
    TaskV1TaskServiceDelete,
    TaskV1TaskServiceSendReminder,
} from 'c1gen/permissions'
import { C1Button, C1IconButton } from 'c1ui'
import { C1ButtonDropdownEndIcon } from 'components/core/button'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useFeatureChecker, useHasPermission, usePermissionChecker } from 'components/no-ui/capability'
import {
    ApproveWithCommentDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/ApproveWithCommentDropdownOption'
import {
    ApproveWithCommentPendingDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/ApproveWithCommentPendingDropdownOption'
import {
    CancelTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/CancelTaskDropdownOption'
import {
    CommentOnTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/CommentOnTaskDropdownOption'
import {
    DeleteTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/DeleteTaskDropdownOption'
import {
    DenyWithCommentDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/DenyWithCommentDropdownOption'
import {
    DenyWithCommentPendingDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/DenyWithCommentPendingDropdownOption'
import {
    HardResetTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/HardResetTaskDropdownOption'
import {
    MarkProvisionCompleteDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/MarkProvisionCompleteDropdownOption'
import {
    ProcessNowDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/ProcessNowDropdownOption'
import {
    ReassignTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/ReassignTaskDropdownOption'
import {
    RestartTaskDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/RestartTaskDropdownOption'
import {
    SendReminderDropdownOption,
} from 'components/pages/task/details/task-content-v2/actions/SendReminderDropdownOption'
import { canActOn } from 'components/pages/task/state'
import { TaskValue } from 'lib/actions/task'
import { MoreHorizontal } from 'lucide-react'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, MouseEvent, useId, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { MarkProvisionErroredDropdownOption } from './MarkProvisionErroredDropdownOption'
import { SkipStepDropdownOption } from './SkipStepDropdownOption'

export enum TaskActionButtonType {
    ellipses,
    actions,
}

export enum TaskActionOptions {
    approveWithComment,
    denyWithComment,
    cancel,
    restart,
    sendReminder,
    delete,
    reassign,
    comment,
    markProvisionErrored,
    hardReset,
    processNow,
    markProvisionComplete,
    skipStep,
    pendingApproveWithComment,
    pendingDenyWithComment,
}

type TaskActionsDropdownV2Props = {
    task: TaskValue
    buttonType?: TaskActionButtonType
    options: TaskActionOptions[]
}

export const TaskActionsDropdownV2: FC<TaskActionsDropdownV2Props> = ({
    task,
    buttonType = TaskActionButtonType.ellipses,
    options,
}) => {
    const permChecker = usePermissionChecker()
    const hasSendReminderFeature = useFeatureChecker(FeatureFlagIdSendReminder)
    const hasSendReminderPermission = useHasPermission(TaskV1TaskServiceSendReminder)
    const intl = useIntl()
    const id = useId()
    const buttonId = `task-actions-button-${id}`
    const menuId = `task-actions-menu-${id}`
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const menuRef = useRef<HTMLDivElement>(null)
    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = (): void => {
        setAnchorEl(null)
    }

    const canAct = {
        approve: permChecker(TaskV1TaskActionsServiceApprove),
        deny: permChecker(TaskV1TaskActionsServiceDeny),
        markProvisionComplete: permChecker(TaskV1TaskActionsServiceMarkProvisionComplete),
        markProvisionErrored: permChecker(TaskV1TaskActionsServiceMarkProvisionErrored),
        comment: permChecker(TaskV1TaskActionsServiceComment),
        reassign: permChecker(TaskV1TaskActionsServiceReassign),
        sendReminder: permChecker(TaskV1TaskServiceSendReminder),
        restart: permChecker(TaskV1TaskActionsServiceRestart),
        cancel: permChecker(TaskV1TaskActionsServiceClose),
        hardReset: permChecker(TaskV1TaskActionsServiceHardReset),
        delete: permChecker(TaskV1TaskServiceDelete),
        processNow: permChecker(TaskV1TaskActionsServiceProcessNow),
        skipStep: permChecker(TaskV1TaskActionsServiceSkipStep),
    }
    const anyTrue = Object.values(canAct).some(Boolean)
    if (!anyTrue) {
        return null
    }

    return (
        <>
            {buttonType === TaskActionButtonType.actions ? (
                <C1Button
                    id={buttonId}
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    endIcon={<C1ButtonDropdownEndIcon />}
                    variant="outlined"
                    sx={{
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                        },
                    }}
                >
                    <FormattedMessage id="components_task_actions_dropdown_button_label" defaultMessage="Actions" />
                </C1Button>
            ) : (
                <C1IconButton
                    id={buttonId}
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    color="info"
                    aria-label={intl.formatMessage({ id: 'components_task_actions_dropdown_button_label', defaultMessage: 'Actions' })}
                    sx={{
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                        },
                    }}
                >
                    <C1Icon icon={MoreHorizontal} />
                </C1IconButton>
            )}
            <Menu
                ref={menuRef}
                id={menuId}
                aria-label={intl.formatMessage({ id: 'components_task_actions_dropdown_menu_label', defaultMessage: 'Actions menu' })}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': buttonId,
                    autoFocusItem: true,
                    variant: 'menu',
                }}
                TransitionProps={{
                    onEntered: (): void => {
                        // Focus first menu item after transition completes and child components have mounted
                        // Use ref to scope query to this specific menu instance
                        if (menuRef.current) {
                            // Small delay to ensure React has finished rendering all child components
                            setTimeout(() => {
                                const firstMenuItem = menuRef.current?.querySelector('[role="menuitem"]') as HTMLElement
                                firstMenuItem?.focus()
                            }, 100)
                        }
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {options.includes(TaskActionOptions.pendingApproveWithComment) && canAct.approve &&
                    canActOn(task, TaskActionType.APPROVE) && (
                    <ApproveWithCommentPendingDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.pendingDenyWithComment) && canAct.deny &&
                    canActOn(task, TaskActionType.DENY) && (
                    <DenyWithCommentPendingDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.approveWithComment) && canAct.approve && canActOn(task, TaskActionType.APPROVE) && (
                    <ApproveWithCommentDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.denyWithComment) && canAct.deny && canActOn(task, TaskActionType.DENY) && (
                    <DenyWithCommentDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.markProvisionComplete) &&
                    canAct.markProvisionComplete &&
                    canActOn(task, TaskActionType.PROVISION_COMPLETE) && (
                    <MarkProvisionCompleteDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.markProvisionErrored) &&
                    canAct.markProvisionErrored &&
                    canActOn(task, TaskActionType.PROVISION_ERRORED) && (
                    <MarkProvisionErroredDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.comment) && canAct.comment && canActOn(task, TaskActionType.COMMENT) && (
                    <CommentOnTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.reassign) && canAct.reassign && canActOn(task, TaskActionType.REASSIGN) && (
                    <ReassignTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.sendReminder) &&
                    canAct.sendReminder &&
                    canActOn(task, TaskActionType.SEND_REMINDER) &&
                    hasSendReminderFeature &&
                    hasSendReminderPermission && (
                    <SendReminderDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.restart) && canAct.restart && canActOn(task, TaskActionType.RESTART) && (
                    <RestartTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.cancel) && canAct.cancel && canActOn(task, TaskActionType.CLOSE) && (
                    <CancelTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.hardReset) && canAct.hardReset && canActOn(task, TaskActionType.HARD_RESET) && (
                    <HardResetTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.delete) && canAct.delete && canActOn(task, TaskActionType.DELETE) && (
                    <DeleteTaskDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.processNow) && canAct.processNow && canActOn(task, TaskActionType.PROCESS_NOW) && (
                    <ProcessNowDropdownOption task={task} close={handleClose} />
                )}
                {options.includes(TaskActionOptions.skipStep) && canAct.skipStep && canActOn(task, TaskActionType.SKIP_STEP) && (
                    <SkipStepDropdownOption task={task} close={handleClose} />
                )}
            </Menu>
        </>
    )
}
